import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSelector } from 'react-redux';
import { selectCurrentUser, updateProfile } from '../features/auth/authSlice';
import { updateUser } from '../features/users/usersApi';
import { useAppDispatch } from '../store/hooks';
import { showToast } from './ui/toast.utils';
import api from '../lib/api';

const schema = z.object({
  teableUrl: z.string().url('URL inválida').or(z.literal('')),
  teableToken: z.string().min(1, 'Token requerido').or(z.literal('')),
  teableSpaceId: z.string().min(1, 'Space ID requerido').or(z.literal('')),
});

type FormValues = z.infer<typeof schema>;

export function TeableConfigForm() {
  const currentUser = useSelector(selectCurrentUser);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [autoProvisioned, setAutoProvisioned] = useState(false);
  const [manualOverride, setManualOverride] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      teableUrl: '',
      teableToken: '',
      teableSpaceId: '',
    },
  });

  const teableUrl = watch('teableUrl');
  const teableSpaceId = watch('teableSpaceId');

  useEffect(() => {
    if (!currentUser?.id) return;
    api.get(`users/${currentUser.id}`)
      .then(({ data }) => {
        const user = data.user ?? data;
        const hasAutoConfig = Boolean(user.teableUrl && user.teableSpaceId);
        setAutoProvisioned(hasAutoConfig);
        reset({
          teableUrl: user.teableUrl || '',
          teableToken: '',
          teableSpaceId: user.teableSpaceId || '',
        });
      })
      .catch(() => { /* no-op: defaults remain empty */ });
  }, [currentUser?.id, reset]);

  const onSubmit = async (data: FormValues) => {
    if (!currentUser) return;
    try {
      setLoading(true);
      const payload: Parameters<typeof updateUser>[1] = {
        teableUrl: data.teableUrl,
        teableSpaceId: data.teableSpaceId,
      };
      if (data.teableToken.trim() !== '') {
        payload.teableToken = data.teableToken.trim();
      }
      const updatedUser = await updateUser(currentUser.id, payload);
      dispatch(updateProfile({
        teableUrl: updatedUser.teableUrl,
        teableSpaceId: updatedUser.teableSpaceId,
      }));
      showToast('Configuración de Teable actualizada', 'success');
      setManualOverride(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al actualizar la configuración';
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const isReadOnly = autoProvisioned && !manualOverride;

  if (isReadOnly) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
          <p className="text-sm font-medium text-green-800 dark:text-green-300">
            Configuración automática activa
          </p>
          <p className="mt-1 text-xs text-green-600 dark:text-green-400">
            La cuenta de Teable fue provisionada automáticamente al crear el usuario.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">URL</span>
            <p className="text-sm text-foreground">{teableUrl || '—'}</p>
          </div>
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Space ID</span>
            <p className="text-sm font-mono text-foreground">{teableSpaceId || '—'}</p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setManualOverride(true)}
          className="text-xs text-muted-foreground underline hover:text-foreground"
        >
          Editar configuración manualmente
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {autoProvisioned && manualOverride && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
          <p className="text-xs text-amber-700 dark:text-amber-300">
            Editando configuración manual. Los cambios reemplazarán la configuración automática.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Teable URL</label>
          <input
            {...register('teableUrl')}
            placeholder="https://teable.tudominio.com"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
          />
          {errors.teableUrl && <p className="text-xs text-destructive">{errors.teableUrl.message}</p>}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Teable Space ID</label>
          <input
            {...register('teableSpaceId')}
            placeholder="spcXXXXXXXX"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
          />
          {errors.teableSpaceId && <p className="text-xs text-destructive">{errors.teableSpaceId.message}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Teable Token</label>
        <input
          {...register('teableToken')}
          type="password"
          placeholder="pat_XXXXXXXX"
          autoComplete="off"
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
        />
        <p className="text-xs text-muted-foreground">
          Opcional si ya guardaste un token: déjalo vacío para no cambiarlo.
        </p>
        {errors.teableToken && <p className="text-xs text-destructive">{errors.teableToken.message}</p>}
      </div>

      <div className="flex items-center gap-3 justify-end">
        {autoProvisioned && (
          <button
            type="button"
            onClick={() => setManualOverride(false)}
            className="text-xs text-muted-foreground underline hover:text-foreground"
          >
            Cancelar
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {loading ? 'Guardando...' : 'Guardar Configuración'}
        </button>
      </div>
    </form>
  );
}
